import {
  ModelConceptRelationship,
  RelationshipMapping,
} from 'src/containers/ProjectDiagram/types/data';
import {
  Model,
  ModelSnapshot,
} from 'src/containers/ProjectDiagram/types/model';
import {
  isMapping,
  isModelConceptRelationship,
} from 'src/containers/ProjectDiagram/utils/data';
import { getAllClassHierarchy } from 'src/containers/ProjectDiagram/utils/getAllClassHierarchy';
import { CreateRelationshipMappingOptions } from 'src/containers/ProjectDiagram/utils/mapping/createRelationshipMapping';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';

export type DomainsAndRanges = Map<
  string | undefined,
  { domains: Set<string | undefined>; ranges: Set<string | undefined> }
>;

export const addDomainsAndRanges =
  (domainsAndRanges: DomainsAndRanges = new Map()) =>
  (linkData: ModelConceptRelationship) => {
    if (!domainsAndRanges.has(linkData.value)) {
      domainsAndRanges.set(linkData.value, {
        domains: new Set(),
        ranges: new Set(),
      });
    }
    const fromIri = linkData.from && getModelConceptClassValue(linkData.from);
    const toIri = linkData.to && getModelConceptClassValue(linkData.to);
    if (fromIri) {
      domainsAndRanges.get(linkData.value)?.domains.add(fromIri);
    }
    if (toIri) {
      domainsAndRanges.get(linkData.value)?.ranges.add(toIri);
    }
  };

export const addDomainsAndRangesFromHierarchy = (
  model: Pick<ModelSnapshot | Model, 'nodeDataArray' | 'linkDataArray'>,
  domainsAndRanges: DomainsAndRanges = new Map()
) => {
  const classHierarchyByIri = getAllClassHierarchy(model);
  domainsAndRanges.forEach(({ domains, ranges }) => {
    Array.from(domains).forEach((domain) => {
      const hierarchy = classHierarchyByIri[domain || ''];
      Object.keys(hierarchy.descendants).forEach((klass) => {
        domains.add(klass);
      });
    });
    Array.from(ranges).forEach((range) => {
      const hierarchy = classHierarchyByIri[range || ''];
      Object.keys(hierarchy.descendants).forEach((klass) => {
        ranges.add(klass);
      });
    });
  });
};

export const getAllDomainsAndRanges = (
  model: Pick<ModelSnapshot | Model, 'nodeDataArray' | 'linkDataArray'>,
  classMappingIriByKey: Map<string, string> = new Map(),
  domainsAndRanges: DomainsAndRanges = new Map()
) => {
  model.linkDataArray.forEach((linkData) => {
    if (isMapping(linkData)) {
      classMappingIriByKey.set(
        linkData.key,
        getModelConceptClassValue(linkData.to || '')
      );
    } else if (isModelConceptRelationship(linkData)) {
      addDomainsAndRanges(domainsAndRanges)(linkData);
    }
  });

  addDomainsAndRangesFromHierarchy(model, domainsAndRanges);

  return {
    classMappingIriByKey,
    domainsAndRanges,
  };
};

export const isValidDomainAndRange = (
  data: { relationshipIri: string; fromIri?: string; toIri?: string },
  domainsAndRanges: DomainsAndRanges = new Map()
) => {
  const relationship = domainsAndRanges.get(data.relationshipIri);
  if (!relationship) {
    return false;
  }

  if (data.fromIri && !relationship.domains.has(data.fromIri)) {
    return false;
  }

  if (data.toIri && !relationship?.ranges.has(data.toIri)) {
    return false;
  }

  return true;
};

export const isValidMappingDomainAndRange = (
  linkData: RelationshipMapping | CreateRelationshipMappingOptions,
  classMappingIriByKey: Map<string, string> = new Map(),
  domainsAndRanges: DomainsAndRanges = new Map()
) =>
  isValidDomainAndRange(
    {
      relationshipIri: linkData.relationshipIri,
      fromIri: linkData.from
        ? classMappingIriByKey.get(linkData.from)
        : linkData.fromClassIri,
      toIri: linkData.to
        ? classMappingIriByKey.get(linkData.to)
        : linkData.toClassIri,
    },
    domainsAndRanges
  );
